import { useEffect, useMemo, useRef, useState } from "react";
import { Building2, Check, ChevronDown, Layers, Lock, Network } from "lucide-react";
import { cn } from "@/lib/cn";
import { Tooltip } from "@/components/atoms/Tooltip";
import { SearchInput } from "@/components/molecules/SearchInput";
import { FilterBarBoundary, useFilterHighlight } from "@/components/molecules/FilterBar";
import {
  useScopeOptions,
  type ScopeBranchOption,
  type ScopeEntityOption,
} from "@/hooks/useScopeOptions";
import { isScopeApplied, sameScope } from "@/lib/scopeParams";
import {
  EMPTY_SCOPE,
  usePageScope,
  useSettingsStore,
  type ScopePageKey,
  type ScopeSelection,
} from "@/store/settingsStore";

interface Props {
  /** Which page's selection this picker owns. Each page keeps its own. */
  page: ScopePageKey;
  /**
   * "both" offers legal entities and branches; "entity" hides the branch column for pages
   * that configure per legal entity and have no branch axis at all.
   */
  mode?: "both" | "entity";
  /** Hover copy on the trigger. Every scope control carries one — see {@link SCOPE_TOOLTIP}. */
  tooltip?: string;
  className?: string;
  /**
   * When true, "All legal entities" ticks every entity in the list and Apply
   * stores those ids — used where the API needs an explicit id list rather than
   * an omitted param (campaigns).
   */
  expandAllEntities?: boolean;
  /** Fired after the selection is committed — pages reload labels from this. */
  onApply?: (scope: ScopeSelection) => void;
}

/**
 * Default hover copy for the scope controls.
 *
 * It lives with the components rather than at each call site so that every place the
 * legal entity / branch can be changed explains itself the same way, including ones added
 * later. Pass `tooltip` to say something more specific.
 */
export const SCOPE_TOOLTIP = "Limit the list to specific legal entities or branches.";
const BRANCH_TOOLTIP = "Choose the legal entity and branch this is sent from.";
const SINGLE_SCOPE_TOOLTIP = "Choose the legal entity and branch this applies to.";

/** Ticked state of a checkbox row. "mixed" = the entity has some of its branches picked. */
type Tick = boolean | "mixed";

function CheckBox({
  state,
  tone,
  shape = "square",
}: {
  state: Tick;
  tone: "entity" | "branch";
  shape?: "square" | "round";
}) {
  const on = state === true;
  const accent = tone === "entity" ? "bg-[#009BF5] border-[#009BF5]" : "bg-[#6D4AEA] border-[#6D4AEA]";
  return (
    <span
      className={cn(
        "flex items-center justify-center w-4 h-4 border shrink-0 text-white",
        shape === "round" ? "rounded-full" : "rounded",
        on ? accent : "border-bridge-border/80",
      )}
    >
      {on && <Check size={11} strokeWidth={3.2} />}
      {state === "mixed" && (
        <span className={cn("w-2 h-[2px] rounded", tone === "entity" ? "bg-[#009BF5]" : "bg-[#6D4AEA]")} />
      )}
    </span>
  );
}

function Row({
  state,
  tone,
  label,
  meta,
  indent = 0,
  shape = "square",
  onClick,
}: {
  state: Tick;
  tone: "entity" | "branch";
  label: string;
  meta?: string;
  indent?: number;
  shape?: "square" | "round";
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role={shape === "round" ? "radio" : "checkbox"}
      aria-checked={state === "mixed" ? "mixed" : state}
      onClick={onClick}
      style={{ paddingLeft: 12 + indent * 14 }}
      className={cn(
        "w-full flex items-center gap-2 pr-3 py-1.5 text-left text-[13px] transition-colors hover:bg-bridge-bg focus-ring",
        state === true && (tone === "entity" ? "text-[#009BF5] font-semibold" : "text-[#6D4AEA] font-semibold"),
        state !== true && "text-bridge-fg",
      )}
    >
      <CheckBox state={state} tone={tone} shape={shape} />
      <span className="flex-1 truncate">{label}</span>
      {meta && <span className="text-[11px] text-bridge-subtle shrink-0">{meta}</span>}
    </button>
  );
}

/**
 * Per-page legal entity + branch filter, replacing the global topbar selectors.
 *
 * Ticking a legal entity takes the whole entity; ticking individual branches narrows it
 * instead, and the entity falls back to a dash. Nothing ticked means everything the user
 * can see. Selections are held until Apply so a multi-tick costs one refetch, not one per
 * tick. Each page keeps its own selection, so narrowing Properties leaves Leads alone.
 */
export function ScopePicker({
  page,
  mode = "both",
  tooltip = SCOPE_TOOLTIP,
  className,
  expandAllEntities = false,
  onApply,
}: Props) {
  const { entities, branches, labels, locked, lockedLabel, loading } = useScopeOptions();
  const scope = usePageScope(page);
  const scopeLabels = useSettingsStore((s) => s.scopeLabels);
  const setScope = useSettingsStore((s) => s.setScope);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  // Held selection: only committed to the store on Apply.
  const [draft, setDraft] = useState<ScopeSelection>(scope);
  const rootRef = useRef<HTMLDivElement>(null);

  const applied = isScopeApplied(scope);
  const highlighted = useFilterHighlight(applied);

  const allEntityIds = useMemo(() => entities.map((e) => e.id).filter(Boolean), [entities]);
  const allEntitiesDraft: ScopeSelection = useMemo(
    () => ({ legalEntityIds: allEntityIds, branchIds: [] }),
    [allEntityIds],
  );
  const allEntitiesSelected =
    allEntityIds.length > 0 &&
    draft.branchIds.length === 0 &&
    allEntityIds.every((id) => draft.legalEntityIds.includes(id));

  // Re-seed the draft whenever the panel opens, so an abandoned edit is discarded and a
  // scope changed on another page is picked up. With expandAllEntities, an empty stored
  // scope is shown as every entity ticked.
  useEffect(() => {
    if (!open) return;
    if (expandAllEntities && !isScopeApplied(scope) && allEntityIds.length > 0) {
      setDraft(allEntitiesDraft);
    } else {
      setDraft(scope);
    }
    setSearch("");
  }, [open, scope, expandAllEntities, allEntityIds, allEntitiesDraft]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const entityById = useMemo(() => new Map(entities.map((e) => [e.id, e])), [entities]);

  // `entities` is a DFS pre-order flattening (see useScopeOptions#flatten), so an entity's
  // descendants are exactly the run that follows it with a greater depth.
  const descendantIds = (id: string): string[] => {
    const idx = entities.findIndex((e) => e.id === id);
    if (idx === -1) return [];
    const depth = entities[idx].depth;
    const ids: string[] = [];
    for (let i = idx + 1; i < entities.length; i++) {
      if (entities[i].depth <= depth) break;
      ids.push(entities[i].id);
    }
    return ids;
  };

  const toggleEntity = (id: string) => {
    // Ticking an entity takes every sub-entity beneath it too, so a parent covers its whole
    // branch of the hierarchy instead of just its own direct branches.
    const groupIds = [id, ...descendantIds(id)];
    const branchIds = groupIds.flatMap((gid) => entityById.get(gid)?.branches.map((b) => b.id) ?? []);
    setDraft((prev) => {
      const on =
        prev.legalEntityIds.includes(id) ||
        branchIds.some((b) => prev.branchIds.includes(b)) ||
        groupIds.some((gid) => prev.legalEntityIds.includes(gid));
      return {
        legalEntityIds: on
          ? prev.legalEntityIds.filter((x) => !groupIds.includes(x))
          : [...new Set([...prev.legalEntityIds, ...groupIds])],
        // Either way the group's individual branch picks go: it is now all-or-nothing.
        branchIds: prev.branchIds.filter((b) => !branchIds.includes(b)),
      };
    });
  };

  const toggleBranch = (branch: ScopeBranchOption) => {
    const entity = entityById.get(branch.legalEntityId);
    const siblings = entity?.branches.map((b) => b.id) ?? [branch.id];
    setDraft((prev) => {
      if (prev.legalEntityIds.includes(branch.legalEntityId)) {
        // The whole entity was taken — switch to explicit branches, minus this one.
        return {
          legalEntityIds: prev.legalEntityIds.filter((x) => x !== branch.legalEntityId),
          branchIds: [...prev.branchIds, ...siblings.filter((b) => b !== branch.id)],
        };
      }
      if (prev.branchIds.includes(branch.id)) {
        return { ...prev, branchIds: prev.branchIds.filter((b) => b !== branch.id) };
      }
      const next = [...prev.branchIds, branch.id];
      // Every branch ticked one by one is the same as the entity: collapse it back so the
      // request carries one entity id instead of a long branch list.
      if (siblings.length > 1 && siblings.every((b) => next.includes(b))) {
        return {
          legalEntityIds: [...prev.legalEntityIds, branch.legalEntityId],
          branchIds: next.filter((b) => !siblings.includes(b)),
        };
      }
      return { ...prev, branchIds: next };
    });
  };

  const entityTick = (id: string): Tick => {
    // Own id being in legalEntityIds isn't enough on its own: toggling a single branch under
    // a descendant strips only that descendant's id, leaving an ancestor's id (added when the
    // whole subtree was ticked together) stale. So "fully checked" is verified against every
    // branch in the subtree, not just this entity's own flag.
    const groupIds = [id, ...descendantIds(id)];
    const allBranches = groupIds.flatMap((gid) => entityById.get(gid)?.branches ?? []);
    const branchSelected = (b: ScopeBranchOption) =>
      draft.branchIds.includes(b.id) || draft.legalEntityIds.includes(b.legalEntityId);

    const fullySelected =
      allBranches.length > 0 ? allBranches.every(branchSelected) : draft.legalEntityIds.includes(id);
    if (fullySelected) return true;

    const partiallyPicked =
      draft.legalEntityIds.includes(id) ||
      allBranches.some(branchSelected) ||
      groupIds.some((gid) => draft.legalEntityIds.includes(gid));
    return partiallyPicked ? "mixed" : false;
  };

  const query = search.trim().toLowerCase();
  const visibleEntities = entities.filter(
    (e) =>
      !query ||
      e.name.toLowerCase().includes(query) ||
      e.branches.some((b) => b.name.toLowerCase().includes(query)),
  );
  const branchGroups = entities
    .map((e) => ({
      entity: e,
      branches: e.branches.filter(
        (b) => !query || e.name.toLowerCase().includes(query) || b.name.toLowerCase().includes(query),
      ),
    }))
    .filter((g) => g.branches.length > 0);

  // A branch reads as picked whether it was ticked directly or swept in by its parent entity,
  // so the count shown next to "Branches" and in the footer has to match that, not just
  // draft.branchIds (which stays empty when a whole entity covers it).
  const checkedBranchCount = branches.filter(
    (b) => draft.branchIds.includes(b.id) || draft.legalEntityIds.includes(b.legalEntityId),
  ).length;

  const nameOf = (id: string) => labels[id] ?? scopeLabels[id] ?? id;

  const chips = [
    ...scope.legalEntityIds.map((id) => ({ id, tone: "entity" as const, name: nameOf(id) })),
    ...scope.branchIds.map((id) => ({ id, tone: "branch" as const, name: nameOf(id) })),
  ];

  const apply = () => {
    const selectingAllEntities =
      expandAllEntities &&
      allEntityIds.length > 0 &&
      draft.branchIds.length === 0 &&
      allEntityIds.every((id) => draft.legalEntityIds.includes(id));
    const next = selectingAllEntities ? EMPTY_SCOPE : draft;
    const picked: Record<string, string> = {};
    for (const id of [...next.legalEntityIds, ...next.branchIds]) picked[id] = nameOf(id);
    setScope(page, next, picked);
    onApply?.(next);
    setOpen(false);
  };

  const applyUnchanged = sameScope(draft, scope);

  if (locked) {
    return (
      <Tooltip side="top" hoverOnly content="You can only see one branch, so there is nothing to change.">
        <div
          className={cn(
            "flex items-center gap-2 px-3 py-1.5 rounded-lg border border-bridge-border/60 bg-slate-50 text-[13px] text-bridge-muted",
            className,
          )}
        >
          <Layers size={14} className="shrink-0" />
          <span className="truncate max-w-[220px]">{lockedLabel ?? "Your branch"}</span>
          <Lock size={12} className="shrink-0 text-bridge-subtle" />
        </div>
      </Tooltip>
    );
  }

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      {/* Hidden while the panel is open: the plate would otherwise cover the list the
          user just opened. */}
      <Tooltip side="top" hoverOnly disabled={open} content={tooltip}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        disabled={loading && entities.length === 0}
        className={cn(
          "flex items-center gap-2 px-3 py-1.5 rounded-lg border text-[13px] transition-colors max-w-full focus-ring",
          highlighted || applied
            ? "border-[#009BF5]/60 bg-[#009BF5]/10"
            : "border-bridge-border/70 bg-white hover:border-bridge-accent",
        )}
      >
        <Layers size={14} className="shrink-0 text-bridge-accent" />
        {chips.length === 0 ? (
          <span className="text-bridge-muted whitespace-nowrap">
            {mode === "entity" ? "All legal entities" : "All entities & branches"}
          </span>
        ) : (
          <span className="flex items-center gap-1.5 overflow-hidden">
            {chips.slice(0, 2).map((chip) => (
              <span
                key={chip.id}
                className={cn(
                  "px-2 py-0.5 rounded-full text-[11.5px] font-semibold border truncate max-w-[130px]",
                  chip.tone === "entity"
                    ? "text-[#0B7FD4] bg-[#009BF5]/10 border-[#009BF5]/40"
                    : "text-[#6D4AEA] bg-[#6D4AEA]/10 border-[#6D4AEA]/35",
                )}
              >
                {chip.name}
              </span>
            ))}
            {chips.length > 2 && (
              <span className="px-2 py-0.5 rounded-full text-[11.5px] font-semibold border border-bridge-border text-bridge-muted">
                +{chips.length - 2}
              </span>
            )}
          </span>
        )}
        <ChevronDown size={12} className={cn("text-bridge-muted transition-transform shrink-0", open && "rotate-180")} />
      </button>
      </Tooltip>

      {open && (
        <FilterBarBoundary>
          <div
            role="dialog"
            aria-label="Choose scope"
            className={cn(
              "absolute top-full left-0 mt-2 bg-white rounded-xl shadow-premium border border-bridge-border overflow-hidden z-50 animate-in fade-in zoom-in duration-150",
              mode === "entity" ? "w-[320px]" : "w-[min(620px,calc(100vw-48px))]",
            )}
          >
            <div className="p-2.5 border-b border-bridge-border/60">
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder={mode === "entity" ? "Search legal entities…" : "Search entities and branches…"}
                autoFocus
              />
            </div>

            <div className={cn("grid", mode === "entity" ? "grid-cols-1" : "grid-cols-2")}>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 px-3 py-2 border-b border-bridge-border/60 bg-[#009BF5]/10 text-[10.5px] font-bold uppercase tracking-widest text-[#0B7FD4]">
                  <Building2 size={12} />
                  Legal entities
                  {draft.legalEntityIds.length > 0 && (
                    <span className="ml-auto normal-case tracking-normal text-[11px]">
                      {draft.legalEntityIds.length} picked
                    </span>
                  )}
                </div>
                <div className="max-h-[264px] overflow-y-auto scrollbar-metal py-1.5">
                  {!query && (
                    <div className="border-b border-bridge-border/60 pb-1.5 mb-1">
                      <Row
                        state={allEntitiesSelected || (!expandAllEntities && draft.legalEntityIds.length === 0 && draft.branchIds.length === 0)}
                        tone="entity"
                        label="All legal entities"
                        onClick={() =>
                          setDraft(expandAllEntities ? allEntitiesDraft : EMPTY_SCOPE)
                        }
                      />
                    </div>
                  )}
                  {visibleEntities.map((entity) => (
                    <Row
                      key={entity.id}
                      state={entityTick(entity.id)}
                      tone="entity"
                      label={entity.name}
                      meta={entity.branches.length ? `${entity.branches.length} br` : undefined}
                      indent={entity.depth}
                      onClick={() => toggleEntity(entity.id)}
                    />
                  ))}
                  {visibleEntities.length === 0 && (
                    <p className="px-3 py-5 text-center text-[12.5px] text-bridge-subtle">No entities match.</p>
                  )}
                </div>
              </div>

              {mode === "both" && (
                <div className="min-w-0 border-l border-bridge-border/60">
                  <div className="flex items-center gap-1.5 px-3 py-2 border-b border-bridge-border/60 bg-[#6D4AEA]/10 text-[10.5px] font-bold uppercase tracking-widest text-[#6D4AEA]">
                    <Network size={12} />
                    Branches
                    {checkedBranchCount > 0 && (
                      <span className="ml-auto normal-case tracking-normal text-[11px]">
                        {checkedBranchCount} picked
                      </span>
                    )}
                  </div>
                  <div className="max-h-[264px] overflow-y-auto scrollbar-metal py-1.5">
                    {branchGroups.map(({ entity, branches: rows }) => (
                      <div key={entity.id}>
                        {/* Named group headers: two entities routinely have a branch of the
                            same name, so a flat list would be ambiguous. */}
                        <div className="flex items-center gap-1.5 px-3 pt-2 pb-1 text-[10.5px] font-bold uppercase tracking-wider text-bridge-subtle">
                          <span className="w-[3px] h-3 rounded-sm bg-[#009BF5]/45 shrink-0" />
                          <span className="truncate">{entity.name}</span>
                        </div>
                        {rows.map((branch) => (
                          <Row
                            key={branch.id}
                            state={
                              draft.branchIds.includes(branch.id) ||
                              draft.legalEntityIds.includes(branch.legalEntityId)
                            }
                            tone="branch"
                            label={branch.name}
                            onClick={() => toggleBranch(branch)}
                          />
                        ))}
                      </div>
                    ))}
                    {branchGroups.length === 0 && (
                      <p className="px-3 py-5 text-center text-[12.5px] text-bridge-subtle">
                        {branches.length === 0 ? "No branches available." : "No branches match."}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 px-3 py-2.5 border-t border-bridge-border/60 bg-bridge-bg/60">
              <span className="flex-1 text-[12.5px] text-bridge-muted">
                {allEntitiesSelected && expandAllEntities ? (
                  <>
                    All <b className="text-bridge-fg font-semibold">{draft.legalEntityIds.length}</b>{" "}
                    legal entities
                  </>
                ) : draft.legalEntityIds.length || checkedBranchCount ? (
                  <>
                    <b className="text-bridge-fg font-semibold">{draft.legalEntityIds.length}</b>{" "}
                    {draft.legalEntityIds.length === 1 ? "entity" : "entities"} ·{" "}
                    <b className="text-bridge-fg font-semibold">{checkedBranchCount}</b>{" "}
                    {checkedBranchCount === 1 ? "branch" : "branches"}
                  </>
                ) : (
                  "Nothing selected — showing everything you can see."
                )}
              </span>
              <button
                type="button"
                onClick={() => setDraft(expandAllEntities ? allEntitiesDraft : EMPTY_SCOPE)}
                className="px-3 py-1.5 rounded-lg border border-bridge-border text-[12.5px] font-semibold text-bridge-muted hover:text-bridge-fg focus-ring"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={apply}
                disabled={applyUnchanged}
                className="px-3 py-1.5 rounded-lg bg-bridge-accent text-white text-[12.5px] font-semibold hover:brightness-105 disabled:opacity-50 disabled:cursor-not-allowed focus-ring"
              >
                Apply
              </button>
            </div>
          </div>
        </FilterBarBoundary>
      )}
    </div>
  );
}

interface BranchScopeSelectProps {
  /** Selected branch id, or "" for none. */
  value: string;
  onChange: (branchId: string, branch: ScopeBranchOption | undefined) => void;
  /**
   * Legal entities ticked in the left column. Empty means "All legal entities".
   * When omitted, the column is a local filter only (Reach Out).
   */
  entityIds?: string[];
  onEntityIdsChange?: (ids: string[]) => void;
  disabled?: boolean;
  /** Renders the trigger in an error state — the field is required and empty on submit. */
  invalid?: boolean;
  placeholder?: string;
  /** Hover copy on the trigger. */
  tooltip?: string;
  className?: string;
}

/**
 * Single-branch picker in the same two-column language as {@link ScopePicker}, for forms
 * that send *from* one branch rather than filter across many.
 *
 * The difference from ScopePicker is what each column does. Here only the branch is the
 * value — round ticks, one at a time, applied on click — while the legal entity column is
 * a local narrowing aid for finding that branch. Ticking an entity therefore changes what
 * the right column offers and nothing else; it never becomes part of what gets submitted.
 */
export function BranchScopeSelect({
  value,
  onChange,
  entityIds,
  onEntityIdsChange,
  disabled,
  invalid,
  placeholder = "Select branch",
  tooltip = BRANCH_TOOLTIP,
  className,
}: BranchScopeSelectProps) {
  const { entities, branches, loading } = useScopeOptions();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  /** Entity ids narrowing the branch column. Local only unless {@link onEntityIdsChange} is set. */
  const [localEntityFilter, setLocalEntityFilter] = useState<string[]>([]);
  const entityFilter = onEntityIdsChange ? (entityIds ?? []) : localEntityFilter;
  const setEntityFilter = (ids: string[]) => {
    if (onEntityIdsChange) onEntityIdsChange(ids);
    else setLocalEntityFilter(ids);
  };
  const rootRef = useRef<HTMLDivElement>(null);

  const selected = branches.find((b) => b.id === value);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) setSearch("");
  }, [open]);

  const query = search.trim().toLowerCase();
  const visibleEntities = entities.filter(
    (e) =>
      !query ||
      e.name.toLowerCase().includes(query) ||
      e.branches.some((b) => b.name.toLowerCase().includes(query)),
  );
  const branchGroups = entities
    .filter((e) => entityFilter.length === 0 || entityFilter.includes(e.id))
    .map((e) => ({
      entity: e,
      branches: e.branches.filter(
        (b) => !query || e.name.toLowerCase().includes(query) || b.name.toLowerCase().includes(query),
      ),
    }))
    .filter((g) => g.branches.length > 0);

  const toggleEntityFilter = (id: string) =>
    setEntityFilter(entityFilter.includes(id) ? entityFilter.filter((x) => x !== id) : [...entityFilter, id]);

  const pick = (branch: ScopeBranchOption) => {
    onChange(branch.id, branch);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <Tooltip side="top" hoverOnly disabled={open} content={tooltip}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled || (loading && branches.length === 0)}
        aria-expanded={open}
        aria-haspopup="dialog"
        className={cn(
          "w-full flex items-center gap-2 px-4 py-2.5 rounded-xl border bg-white text-sm text-left transition-colors focus-ring",
          disabled && "opacity-60 cursor-not-allowed bg-slate-50",
          invalid ? "border-red-400" : "border-bridge-border/70 hover:border-bridge-accent",
        )}
      >
        {selected ? (
          <span className="flex items-center gap-1.5 min-w-0">
            {/* Entity before branch, in their own colours: two entities routinely have a
                branch of the same name, so the branch alone is ambiguous. */}
            <span className="px-2 py-0.5 rounded-full text-[11.5px] font-semibold border truncate max-w-[150px] text-[#0B7FD4] bg-[#009BF5]/10 border-[#009BF5]/40">
              {selected.legalEntityName}
            </span>
            <span className="px-2 py-0.5 rounded-full text-[11.5px] font-semibold border truncate max-w-[150px] text-[#6D4AEA] bg-[#6D4AEA]/10 border-[#6D4AEA]/35">
              {selected.name}
            </span>
          </span>
        ) : entityFilter.length === 0 ? (
          <span className="text-bridge-muted">{placeholder}</span>
        ) : entityFilter.length === 1 ? (
          <span className="px-2 py-0.5 rounded-full text-[11.5px] font-semibold border truncate max-w-[220px] text-[#0B7FD4] bg-[#009BF5]/10 border-[#009BF5]/40">
            {entities.find((e) => e.id === entityFilter[0])?.name ?? "1 legal entity"}
          </span>
        ) : (
          <span className="text-sm text-bridge-fg">{entityFilter.length} legal entities</span>
        )}
        <ChevronDown
          size={14}
          className={cn("ml-auto shrink-0 text-bridge-muted transition-transform", open && "rotate-180")}
        />
      </button>
      </Tooltip>

      {open && (
        <FilterBarBoundary>
          <div
            role="dialog"
            aria-label="Choose branch"
            className="absolute top-full left-0 mt-2 w-[min(620px,calc(100vw-48px))] bg-white rounded-xl shadow-premium border border-bridge-border overflow-hidden z-50 animate-in fade-in zoom-in duration-150"
          >
            <div className="p-2.5 border-b border-bridge-border/60">
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search entities and branches…"
                autoFocus
              />
            </div>

            <div className="grid grid-cols-2">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 px-3 py-2 border-b border-bridge-border/60 bg-[#009BF5]/10 text-[10.5px] font-bold uppercase tracking-widest text-[#0B7FD4]">
                  <Building2 size={12} />
                  Narrow by entity
                </div>
                <div className="max-h-[264px] overflow-y-auto scrollbar-metal py-1.5">
                  {!query && (
                    <div className="border-b border-bridge-border/60 pb-1.5 mb-1">
                      <Row
                        state={entityFilter.length === 0}
                        tone="entity"
                        label="All legal entities"
                        onClick={() => setEntityFilter([])}
                      />
                    </div>
                  )}
                  {visibleEntities.map((entity) => (
                    <Row
                      key={entity.id}
                      state={entityFilter.includes(entity.id)}
                      tone="entity"
                      label={entity.name}
                      meta={entity.branches.length ? `${entity.branches.length} br` : undefined}
                      indent={entity.depth}
                      onClick={() => toggleEntityFilter(entity.id)}
                    />
                  ))}
                  {visibleEntities.length === 0 && (
                    <p className="px-3 py-5 text-center text-[12.5px] text-bridge-subtle">No entities match.</p>
                  )}
                </div>
              </div>

              <div className="min-w-0 border-l border-bridge-border/60">
                <div className="flex items-center gap-1.5 px-3 py-2 border-b border-bridge-border/60 bg-[#6D4AEA]/10 text-[10.5px] font-bold uppercase tracking-widest text-[#6D4AEA]">
                  <Network size={12} />
                  Branch
                  <span className="ml-auto normal-case tracking-normal text-[11px]">pick one</span>
                </div>
                <div className="max-h-[264px] overflow-y-auto scrollbar-metal py-1.5">
                  {branchGroups.map(({ entity, branches: rows }) => (
                    <div key={entity.id}>
                      <div className="flex items-center gap-1.5 px-3 pt-2 pb-1 text-[10.5px] font-bold uppercase tracking-wider text-bridge-subtle">
                        <span className="w-[3px] h-3 rounded-sm bg-[#009BF5]/45 shrink-0" />
                        <span className="truncate">{entity.name}</span>
                      </div>
                      {rows.map((branch) => (
                        <Row
                          key={branch.id}
                          state={branch.id === value}
                          tone="branch"
                          shape="round"
                          label={branch.name}
                          onClick={() => pick(branch)}
                        />
                      ))}
                    </div>
                  ))}
                  {branchGroups.length === 0 && (
                    <p className="px-3 py-5 text-center text-[12.5px] text-bridge-subtle">
                      {branches.length === 0 ? "No branches available." : "No branches match."}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {value && (
              <div className="flex items-center gap-2 px-3 py-2.5 border-t border-bridge-border/60 bg-bridge-bg/60">
                <span className="flex-1 text-[12.5px] text-bridge-muted truncate">
                  Sending from <b className="text-bridge-fg font-semibold">{selected?.name ?? value}</b>
                </span>
                <button
                  type="button"
                  onClick={() => {
                    onChange("", undefined);
                    setOpen(false);
                  }}
                  className="px-3 py-1.5 rounded-lg border border-bridge-border text-[12.5px] font-semibold text-bridge-muted hover:text-bridge-fg focus-ring"
                >
                  Clear
                </button>
              </div>
            )}
          </div>
        </FilterBarBoundary>
      )}
    </div>
  );
}

/** One legal entity, and optionally one of its branches. `branchId: ""` = the whole entity. */
export interface SingleScope {
  legalEntityId: string;
  legalEntityName: string;
  branchId: string;
  branchName: string;
}

interface EntityBranchSelectProps {
  legalEntityId: string | null;
  /** Selected branch id, or "" / null for entity-wide ("All branches"). */
  branchId: string | null;
  onChange: (scope: SingleScope) => void;
  disabled?: boolean;
  invalid?: boolean;
  placeholder?: string;
  tooltip?: string;
  className?: string;
}

/**
 * One legal entity plus an optional branch, in the same two-column language as
 * {@link ScopePicker}.
 *
 * Where ScopePicker filters a list across many entities and BranchScopeSelect insists on
 * exactly one branch, this sits between them: a record is written into one entity, either
 * across all of its branches or into a single one. Both columns are therefore part of the
 * value — picking an entity keeps "All branches", picking a branch pins it and adopts that
 * branch's entity — and each click applies straight away, because a form field with its own
 * Apply button reads as a second submit.
 */
export function EntityBranchSelect({
  legalEntityId,
  branchId,
  onChange,
  disabled,
  invalid,
  placeholder = "Select a legal entity",
  tooltip = SINGLE_SCOPE_TOOLTIP,
  className,
}: EntityBranchSelectProps) {
  const { entities, branches, loading } = useScopeOptions();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);

  const selectedEntity = entities.find((e) => e.id === legalEntityId);
  const selectedBranch = branches.find((b) => b.id === branchId);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) setSearch("");
  }, [open]);

  const query = search.trim().toLowerCase();
  const visibleEntities = entities.filter(
    (e) =>
      !query ||
      e.name.toLowerCase().includes(query) ||
      e.branches.some((b) => b.name.toLowerCase().includes(query)),
  );
  // Once an entity is chosen the branch column is confined to it: the pair has to stay
  // consistent, and a branch from elsewhere would silently move the entity too.
  const branchGroups = entities
    .filter((e) => !legalEntityId || e.id === legalEntityId)
    .map((e) => ({
      entity: e,
      branches: e.branches.filter(
        (b) => !query || e.name.toLowerCase().includes(query) || b.name.toLowerCase().includes(query),
      ),
    }))
    .filter((g) => g.branches.length > 0);

  const pickEntity = (entity: ScopeEntityOption) => {
    onChange({
      legalEntityId: entity.id,
      legalEntityName: entity.name,
      branchId: "",
      branchName: "",
    });
  };

  const pickBranch = (branch: ScopeBranchOption) => {
    onChange({
      legalEntityId: branch.legalEntityId,
      legalEntityName: branch.legalEntityName,
      branchId: branch.id,
      branchName: branch.name,
    });
    setOpen(false);
  };

  const clearBranch = () => {
    if (!selectedEntity) return;
    onChange({
      legalEntityId: selectedEntity.id,
      legalEntityName: selectedEntity.name,
      branchId: "",
      branchName: "",
    });
  };

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <Tooltip side="top" hoverOnly disabled={open} content={tooltip}>
        <button
          type="button"
          onClick={() => !disabled && setOpen((v) => !v)}
          disabled={disabled || (loading && entities.length === 0)}
          aria-expanded={open}
          aria-haspopup="dialog"
          className={cn(
            "w-full flex items-center gap-2 px-3 py-2 rounded-lg border bg-white text-sm text-left transition-colors focus-ring",
            disabled && "opacity-60 cursor-not-allowed bg-slate-50",
            invalid ? "border-red-400" : "border-bridge-border/70 hover:border-bridge-accent",
          )}
        >
          <Layers size={14} className="shrink-0 text-bridge-accent" />
          {selectedEntity ? (
            <span className="flex items-center gap-1.5 min-w-0">
              <span className="px-2 py-0.5 rounded-full text-[11.5px] font-semibold border truncate max-w-[150px] text-[#0B7FD4] bg-[#009BF5]/10 border-[#009BF5]/40">
                {selectedEntity.name}
              </span>
              <span
                className={cn(
                  "px-2 py-0.5 rounded-full text-[11.5px] font-semibold border truncate max-w-[150px]",
                  selectedBranch
                    ? "text-[#6D4AEA] bg-[#6D4AEA]/10 border-[#6D4AEA]/35"
                    : "text-bridge-muted bg-bridge-bg border-bridge-border",
                )}
              >
                {selectedBranch?.name ?? "All branches"}
              </span>
            </span>
          ) : (
            <span className="text-bridge-muted">{placeholder}</span>
          )}
          <ChevronDown
            size={14}
            className={cn("ml-auto shrink-0 text-bridge-muted transition-transform", open && "rotate-180")}
          />
        </button>
      </Tooltip>

      {open && (
        <FilterBarBoundary>
          <div
            role="dialog"
            aria-label="Choose legal entity and branch"
            className="absolute top-full left-0 mt-2 w-[min(620px,calc(100vw-48px))] bg-white rounded-xl shadow-premium border border-bridge-border overflow-hidden z-50 animate-in fade-in zoom-in duration-150"
          >
            <div className="p-2.5 border-b border-bridge-border/60">
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search entities and branches…"
                autoFocus
              />
            </div>

            <div className="grid grid-cols-2">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 px-3 py-2 border-b border-bridge-border/60 bg-[#009BF5]/10 text-[10.5px] font-bold uppercase tracking-widest text-[#0B7FD4]">
                  <Building2 size={12} />
                  Legal entity
                  <span className="ml-auto normal-case tracking-normal text-[11px]">pick one</span>
                </div>
                <div className="max-h-[264px] overflow-y-auto scrollbar-metal py-1.5">
                  {visibleEntities.map((entity) => (
                    <Row
                      key={entity.id}
                      state={entity.id === legalEntityId}
                      tone="entity"
                      shape="round"
                      label={entity.name}
                      meta={entity.branches.length ? `${entity.branches.length} br` : undefined}
                      indent={entity.depth}
                      onClick={() => pickEntity(entity)}
                    />
                  ))}
                  {visibleEntities.length === 0 && (
                    <p className="px-3 py-5 text-center text-[12.5px] text-bridge-subtle">No entities match.</p>
                  )}
                </div>
              </div>

              <div className="min-w-0 border-l border-bridge-border/60">
                <div className="flex items-center gap-1.5 px-3 py-2 border-b border-bridge-border/60 bg-[#6D4AEA]/10 text-[10.5px] font-bold uppercase tracking-widest text-[#6D4AEA]">
                  <Network size={12} />
                  Branch
                  <span className="ml-auto normal-case tracking-normal text-[11px]">optional</span>
                </div>
                <div className="max-h-[264px] overflow-y-auto scrollbar-metal py-1.5">
                  {selectedEntity && !query && (
                    <div className="border-b border-bridge-border/60 pb-1.5 mb-1">
                      <Row
                        state={!branchId}
                        tone="branch"
                        shape="round"
                        label="All branches"
                        onClick={clearBranch}
                      />
                    </div>
                  )}
                  {branchGroups.map(({ entity, branches: rows }) => (
                    <div key={entity.id}>
                      <div className="flex items-center gap-1.5 px-3 pt-2 pb-1 text-[10.5px] font-bold uppercase tracking-wider text-bridge-subtle">
                        <span className="w-[3px] h-3 rounded-sm bg-[#009BF5]/45 shrink-0" />
                        <span className="truncate">{entity.name}</span>
                      </div>
                      {rows.map((branch) => (
                        <Row
                          key={branch.id}
                          state={branch.id === branchId}
                          tone="branch"
                          shape="round"
                          label={branch.name}
                          onClick={() => pickBranch(branch)}
                        />
                      ))}
                    </div>
                  ))}
                  {branchGroups.length === 0 && (
                    <p className="px-3 py-5 text-center text-[12.5px] text-bridge-subtle">
                      {!legalEntityId
                        ? "Pick a legal entity to see its branches."
                        : branches.length === 0
                          ? "No branches available."
                          : "No branches match."}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 px-3 py-2.5 border-t border-bridge-border/60 bg-bridge-bg/60">
              <span className="flex-1 text-[12.5px] text-bridge-muted truncate">
                {selectedEntity ? (
                  <>
                    <b className="text-bridge-fg font-semibold">{selectedEntity.name}</b> ·{" "}
                    {selectedBranch?.name ?? "all branches"}
                  </>
                ) : (
                  "Nothing selected yet."
                )}
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-3 py-1.5 rounded-lg border border-bridge-border text-[12.5px] font-semibold text-bridge-muted hover:text-bridge-fg focus-ring"
              >
                Done
              </button>
            </div>
          </div>
        </FilterBarBoundary>
      )}
    </div>
  );
}
